import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { ProgramDirectoryReportRow } from '../../../swagger'
import { programs } from '../../../api/swagger'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import { useMountEffect } from '../../../hooks/useMountEffect'
import LoadingProgress from '../../Elements/LoadingProgress'
import EmptyPage from '../../Elements/EmptyPage'
import { Box, IconButton, Typography } from '@mui/material'
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer'
import ProgramDirectoryReportPDF from './ProgramDirectoryReportPDF'
import { Page } from '../../Elements/PageMargins'
import TitleContext from '../../../TitleContext'
import useEndpoint from '../../../hooks/useEndpoint'
import { LoadingContext } from '../../Context/LoadingContext'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import PdfIcon from '@mui/icons-material/PictureAsPdf'
import { CanAccess } from '../../Elements/Access'

const title = 'Communities'

export const ProgramDirectoryReportPDFPreview: React.FC = () => {
  const { communityKey, programKey } = useParams()
  const { t } = useTranslation()
  const {
    EnrollmentsTable: {
      fetchProgramDirectoryReport: fetchProgramDirectoryReportLoadingId,
    },
  } = useLoadingIds()
  const { loadingIds } = useContext(LoadingContext)
  const { useTitleEffect } = useContext(TitleContext)
  const navigate = useNavigate()
  useTitleEffect(title)

  const [rows, setRows] = React.useState<ProgramDirectoryReportRow[]>()
  const [communityName, setCommunityName] = React.useState<string>()

  const userAgent = navigator.userAgent.toLowerCase()
  const isMobileBrowser =
    /iphone|ipad|ipod|android|blackberry|windows phone|ipad|tablet|playbook|silk/g.test(
      userAgent
    )

  const { triggerFetch: triggerFetchProgramDirectoryReport } = useEndpoint<
    Promise<void>,
    []
  >({
    loadingId: fetchProgramDirectoryReportLoadingId,
    async swaggerCall() {
      const fetchedReport = await programs.fetchProgramDirectoryReport({
        programId: +(programKey as unknown as number),
      })
      setCommunityName(fetchedReport.communityName)
      setRows(fetchedReport.data)
    },
    /**
     * The spinner shows until `rows` is set, so a failed request has to land on a
     * value too, otherwise the page has no way out of the loading state.
     */
    failureCallback() {
      setRows([])
    },
  })

  useMountEffect(() => {
    triggerFetchProgramDirectoryReport()
  })

  if (
    loadingIds.has(fetchProgramDirectoryReportLoadingId) ||
    rows === undefined
  ) {
    return <LoadingProgress />
  }

  if (rows.length === 0) {
    return (
      <EmptyPage
        message={
          <Typography variant="subtitle1" component="p">
            {t(
              'ProgramDirectoryReportPDFPreview.PDF.Preview.ErrorMessage',
              'No families found. Unable to generate the PDF report. Please try again later.'
            )}
          </Typography>
        }
      />
    )
  }

  const breadcrumbs = [
    {
      label: t(
        'ProgramDirectoryReportPDFPreview.Breadcrumb.Communities',
        'Communities'
      ),
      onClick: () => navigate(`/communities`),
    },
    {
      label:
        communityName ??
        t('ProgramDirectoryReportPDFPreview.Breadcrumb.Community', 'Community'),
      onClick: () =>
        navigate(
          `/communities/community-details/${communityKey}/programs/program-details/${programKey}/enrollments`
        ),
    },
    {
      label: t(
        'ProgramDirectoryReportPDFPreview.Breadcrumb.ProgramDirectoryReport',
        'Program Directory Report'
      ),
    },
  ]

  return (
    <CanAccess I="view" on="Program">
      <Page>
        <DynamicBreadcrumbs breadcrumbs={breadcrumbs} />

        <Box display="flex" flexDirection={'column'} height={'70vh'}>
          <Box position="relative" width="100%" height="100%">
            <Box
              position="absolute"
              width="100%"
              height="100%"
              top={10}
              zIndex={2}
            >
              {isMobileBrowser && (
                <Box
                  justifyContent="center"
                  alignItems="center"
                  display="flex"
                  flexDirection="column"
                  textAlign="center"
                >
                  <Typography>
                    {t(
                      'ProgramDirectoryReportPDFPreview.PDF.Preview.PreviewNotAvailableMessage',
                      'Preview not available on mobile devices, please download the PDF clicking the button below.'
                    )}
                  </Typography>
                  <PDFDownloadLink
                    document={
                      <ProgramDirectoryReportPDF
                        rows={rows}
                        communityName={communityName}
                      />
                    }
                    fileName="program-directory.pdf"
                  >
                    <IconButton color="success">
                      <PdfIcon />
                      <Typography>
                        {t(
                          'ProgramDirectoryReportPDFPreview.PDF.Preview.DownloadPDF',
                          'Download PDF'
                        )}
                      </Typography>
                    </IconButton>
                  </PDFDownloadLink>
                </Box>
              )}
              <PDFViewer style={{ flex: 1, width: '100%', height: '100%' }}>
                <ProgramDirectoryReportPDF
                  rows={rows}
                  communityName={communityName}
                />
              </PDFViewer>
            </Box>
          </Box>
        </Box>
      </Page>
    </CanAccess>
  )
}

export default ProgramDirectoryReportPDFPreview
